// Ambient Sound Generator using Web Audio API
// Procedurally generates nature/ambient sounds

const AudioCtx = window.AudioContext || window.webkitAudioContext;
let ctx = null;
const activeNodes = {};

function getCtx() {
  if (!ctx) ctx = new AudioCtx();
  if (ctx.state === 'suspended') ctx.resume();
  return ctx;
}

function createNoise(type = 'white') {
  const c = getCtx();
  const bufferSize = 2 * c.sampleRate;
  const buffer = c.createBuffer(1, bufferSize, c.sampleRate);
  const data = buffer.getChannelData(0);
  
  if (type === 'white') {
    for (let i = 0; i < bufferSize; i++) data[i] = Math.random() * 2 - 1;
  } else if (type === 'brown') {
    let last = 0;
    for (let i = 0; i < bufferSize; i++) {
      const w = Math.random() * 2 - 1;
      data[i] = (last + (0.02 * w)) / 1.02;
      last = data[i];
      data[i] *= 3.5;
    }
  } else if (type === 'pink') {
    let b0 = 0, b1 = 0, b2 = 0, b3 = 0, b4 = 0, b5 = 0, b6 = 0;
    for (let i = 0; i < bufferSize; i++) {
      const w = Math.random() * 2 - 1;
      b0 = 0.99886 * b0 + w * 0.0555179;
      b1 = 0.99332 * b1 + w * 0.0750759;
      b2 = 0.96900 * b2 + w * 0.1538520;
      b3 = 0.86650 * b3 + w * 0.3104856;
      b4 = 0.55000 * b4 + w * 0.5329522;
      b5 = -0.7616 * b5 - w * 0.0168980;
      data[i] = b0 + b1 + b2 + b3 + b4 + b5 + b6 + w * 0.5362;
      data[i] *= 0.11;
      b6 = w * 0.115926;
    }
  }
  
  const source = c.createBufferSource();
  source.buffer = buffer;
  source.loop = true;
  return source;
}

// Rain: filtered brown noise + random high-freq plinks
function startRain(volume) {
  const c = getCtx();
  const master = c.createGain();
  master.gain.value = volume * 0.4;
  master.connect(c.destination);
  
  // Base rain - brown noise with bandpass
  const noise = createNoise('brown');
  const filter = c.createBiquadFilter();
  filter.type = 'bandpass';
  filter.frequency.value = 800;
  filter.Q.value = 0.5;
  noise.connect(filter);
  filter.connect(master);
  noise.start();
  
  // Higher rain texture
  const noise2 = createNoise('pink');
  const filter2 = c.createBiquadFilter();
  filter2.type = 'highpass';
  filter2.frequency.value = 2000;
  const gain2 = c.createGain();
  gain2.gain.value = 0.3;
  noise2.connect(filter2);
  filter2.connect(gain2);
  gain2.connect(master);
  noise2.start();
  
  return { nodes: [noise, noise2], master };
}

// Cafe: low rumble + occasional murmur texture
function startCafe(volume) {
  const c = getCtx();
  const master = c.createGain();
  master.gain.value = volume * 0.3;
  master.connect(c.destination);
  
  const noise = createNoise('brown');
  const filter = c.createBiquadFilter();
  filter.type = 'lowpass';
  filter.frequency.value = 400;
  noise.connect(filter);
  filter.connect(master);
  noise.start();
  
  // Murmur layer
  const noise2 = createNoise('pink');
  const filter2 = c.createBiquadFilter();
  filter2.type = 'bandpass';
  filter2.frequency.value = 600;
  filter2.Q.value = 1.5;
  const gain2 = c.createGain();
  gain2.gain.value = 0.4;
  noise2.connect(filter2);
  filter2.connect(gain2);
  gain2.connect(master);
  noise2.start();
  
  return { nodes: [noise, noise2], master };
}

// Lo-fi: warm filtered noise with gentle oscillation
function startLofi(volume) {
  const c = getCtx();
  const master = c.createGain();
  master.gain.value = volume * 0.25;
  master.connect(c.destination);
  
  const noise = createNoise('pink');
  const filter = c.createBiquadFilter();
  filter.type = 'lowpass';
  filter.frequency.value = 600;
  
  // Gentle LFO on filter
  const lfo = c.createOscillator();
  lfo.frequency.value = 0.1;
  const lfoGain = c.createGain();
  lfoGain.gain.value = 200;
  lfo.connect(lfoGain);
  lfoGain.connect(filter.frequency);
  lfo.start();
  
  noise.connect(filter);
  filter.connect(master);
  noise.start();
  
  // Soft hum
  const osc = c.createOscillator();
  osc.type = 'sine';
  osc.frequency.value = 120;
  const oscGain = c.createGain();
  oscGain.gain.value = 0.04;
  osc.connect(oscGain);
  oscGain.connect(master);
  osc.start();
  
  return { nodes: [noise, lfo, osc], master };
}

// Fireplace: crackling brown noise
function startFireplace(volume) {
  const c = getCtx();
  const master = c.createGain();
  master.gain.value = volume * 0.35;
  master.connect(c.destination);
  
  const noise = createNoise('brown');
  const filter = c.createBiquadFilter();
  filter.type = 'bandpass';
  filter.frequency.value = 1500;
  filter.Q.value = 0.8;
  noise.connect(filter);
  filter.connect(master);
  noise.start();
  
  // Crackle - high noise bursts
  const noise2 = createNoise('white');
  const filter2 = c.createBiquadFilter();
  filter2.type = 'highpass';
  filter2.frequency.value = 4000;
  const gain2 = c.createGain();
  gain2.gain.value = 0.15;
  noise2.connect(filter2);
  filter2.connect(gain2);
  gain2.connect(master);
  noise2.start();
  
  return { nodes: [noise, noise2], master };
}

// Forest: gentle broadband with bird-like tones
function startForest(volume) {
  const c = getCtx();
  const master = c.createGain();
  master.gain.value = volume * 0.2;
  master.connect(c.destination);
  
  const noise = createNoise('pink');
  const filter = c.createBiquadFilter();
  filter.type = 'bandpass';
  filter.frequency.value = 1000;
  filter.Q.value = 0.3;
  noise.connect(filter);
  filter.connect(master);
  noise.start();
  
  return { nodes: [noise], master };
}

// Ocean: slow-modulated brown noise
function startOcean(volume) {
  const c = getCtx();
  const master = c.createGain();
  master.gain.value = volume * 0.35;
  master.connect(c.destination);
  
  const noise = createNoise('brown');
  const filter = c.createBiquadFilter();
  filter.type = 'lowpass';
  filter.frequency.value = 500;
  
  // Wave LFO
  const lfo = c.createOscillator();
  lfo.frequency.value = 0.08;
  const lfoGain = c.createGain();
  lfoGain.gain.value = 300;
  lfo.connect(lfoGain);
  lfoGain.connect(filter.frequency);
  lfo.start();
  
  noise.connect(filter);
  filter.connect(master);
  noise.start();
  
  // Foam layer
  const noise2 = createNoise('white');
  const filter2 = c.createBiquadFilter();
  filter2.type = 'bandpass';
  filter2.frequency.value = 3000;
  filter2.Q.value = 2;
  const gain2 = c.createGain();
  gain2.gain.value = 0.08;
  
  const lfo2 = c.createOscillator();
  lfo2.frequency.value = 0.08;
  const lfo2Gain = c.createGain();
  lfo2Gain.gain.value = 0.08;
  lfo2.connect(lfo2Gain);
  lfo2Gain.connect(gain2.gain);
  lfo2.start();
  
  noise2.connect(filter2);
  filter2.connect(gain2);
  gain2.connect(master);
  noise2.start();
  
  return { nodes: [noise, lfo, noise2, lfo2], master };
}

// Wind: filtered noise with slow modulation
function startWind(volume) {
  const c = getCtx();
  const master = c.createGain();
  master.gain.value = volume * 0.25;
  master.connect(c.destination);
  
  const noise = createNoise('pink');
  const filter = c.createBiquadFilter();
  filter.type = 'bandpass';
  filter.frequency.value = 400;
  filter.Q.value = 0.5;
  
  const lfo = c.createOscillator();
  lfo.frequency.value = 0.05;
  const lfoGain = c.createGain();
  lfoGain.gain.value = 200;
  lfo.connect(lfoGain);
  lfoGain.connect(filter.frequency);
  lfo.start();
  
  noise.connect(filter);
  filter.connect(master);
  noise.start();
  
  return { nodes: [noise, lfo], master };
}

// Birds: sine oscillators with random chirps
function startBirds(volume) {
  const c = getCtx();
  const master = c.createGain();
  master.gain.value = volume * 0.15;
  master.connect(c.destination);
  
  // Gentle background
  const noise = createNoise('pink');
  const filter = c.createBiquadFilter();
  filter.type = 'bandpass';
  filter.frequency.value = 3000;
  filter.Q.value = 3;
  const nGain = c.createGain();
  nGain.gain.value = 0.3;
  noise.connect(filter);
  filter.connect(nGain);
  nGain.connect(master);
  noise.start();
  
  // Chirp oscillators
  const chirpNodes = [];
  for (let i = 0; i < 3; i++) {
    const osc = c.createOscillator();
    osc.type = 'sine';
    osc.frequency.value = 2000 + Math.random() * 2000;
    const g = c.createGain();
    g.gain.value = 0;
    osc.connect(g);
    g.connect(master);
    osc.start();
    chirpNodes.push({ osc, gain: g });
    
    // Random chirps
    const chirp = () => {
      const now = c.currentTime;
      osc.frequency.setValueAtTime(2000 + Math.random() * 3000, now);
      g.gain.setValueAtTime(0, now);
      g.gain.linearRampToValueAtTime(0.3, now + 0.05);
      g.gain.linearRampToValueAtTime(0, now + 0.15);
      osc.frequency.linearRampToValueAtTime(osc.frequency.value + (Math.random() - 0.5) * 1000, now + 0.1);
      setTimeout(chirp, 1000 + Math.random() * 4000);
    };
    setTimeout(chirp, Math.random() * 3000);
  }
  
  return { nodes: [noise, ...chirpNodes.map(c => c.osc)], master };
}

// Holiday: gentle bells with sparkle
function startHoliday(volume) {
  const c = getCtx();
  const master = c.createGain();
  master.gain.value = volume * 0.2;
  master.connect(c.destination);
  const nodes = [];
  // Gentle bell tones
  [523, 659, 784, 1047].forEach((freq, i) => {
    const osc = c.createOscillator();
    osc.type = 'sine';
    osc.frequency.value = freq;
    const g = c.createGain();
    g.gain.value = 0;
    osc.connect(g);
    g.connect(master);
    osc.start();
    nodes.push(osc);
    const ring = () => {
      const now = c.currentTime;
      g.gain.setValueAtTime(0, now);
      g.gain.linearRampToValueAtTime(0.15, now + 0.1);
      g.gain.exponentialRampToValueAtTime(0.001, now + 1.5);
      setTimeout(ring, 3000 + Math.random() * 5000);
    };
    setTimeout(ring, i * 1000 + Math.random() * 2000);
  });
  // Soft snow noise
  const noise = createNoise('white');
  const f = c.createBiquadFilter();
  f.type = 'lowpass'; f.frequency.value = 200;
  const ng = c.createGain(); ng.gain.value = 0.3;
  noise.connect(f); f.connect(ng); ng.connect(master); noise.start();
  nodes.push(noise);
  return { nodes, master };
}

// Spring: gentle rain + soft birds
function startSpring(volume) {
  const c = getCtx();
  const master = c.createGain();
  master.gain.value = volume * 0.25;
  master.connect(c.destination);
  const n1 = createNoise('pink');
  const f1 = c.createBiquadFilter(); f1.type = 'bandpass'; f1.frequency.value = 1200; f1.Q.value = 0.5;
  const g1 = c.createGain(); g1.gain.value = 0.5;
  n1.connect(f1); f1.connect(g1); g1.connect(master); n1.start();
  const n2 = createNoise('white');
  const f2 = c.createBiquadFilter(); f2.type = 'bandpass'; f2.frequency.value = 4000; f2.Q.value = 3;
  const g2 = c.createGain(); g2.gain.value = 0.1;
  n2.connect(f2); f2.connect(g2); g2.connect(master); n2.start();
  return { nodes: [n1, n2], master };
}

// Summer: waves + warm hum
function startSummer(volume) {
  const c = getCtx();
  const master = c.createGain();
  master.gain.value = volume * 0.3;
  master.connect(c.destination);
  const n = createNoise('brown');
  const f = c.createBiquadFilter(); f.type = 'lowpass'; f.frequency.value = 400;
  const lfo = c.createOscillator(); lfo.frequency.value = 0.06;
  const lg = c.createGain(); lg.gain.value = 250;
  lfo.connect(lg); lg.connect(f.frequency); lfo.start();
  n.connect(f); f.connect(master); n.start();
  // Warm hum
  const osc = c.createOscillator(); osc.type = 'sine'; osc.frequency.value = 80;
  const og = c.createGain(); og.gain.value = 0.03;
  osc.connect(og); og.connect(master); osc.start();
  return { nodes: [n, lfo, osc], master };
}

// Autumn: rustling leaves + gentle wind
function startAutumn(volume) {
  const c = getCtx();
  const master = c.createGain();
  master.gain.value = volume * 0.25;
  master.connect(c.destination);
  const n = createNoise('pink');
  const f = c.createBiquadFilter(); f.type = 'bandpass'; f.frequency.value = 600; f.Q.value = 0.8;
  const lfo = c.createOscillator(); lfo.frequency.value = 0.15;
  const lg = c.createGain(); lg.gain.value = 300;
  lfo.connect(lg); lg.connect(f.frequency); lfo.start();
  n.connect(f); f.connect(master); n.start();
  // Crinkle layer
  const n2 = createNoise('white');
  const f2 = c.createBiquadFilter(); f2.type = 'highpass'; f2.frequency.value = 5000;
  const g2 = c.createGain(); g2.gain.value = 0.06;
  n2.connect(f2); f2.connect(g2); g2.connect(master); n2.start();
  return { nodes: [n, lfo, n2], master };
}

// Night: crickets + gentle wind
function startNight(volume) {
  const c = getCtx();
  const master = c.createGain();
  master.gain.value = volume * 0.2;
  master.connect(c.destination);
  // Low wind
  const n = createNoise('brown');
  const f = c.createBiquadFilter(); f.type = 'lowpass'; f.frequency.value = 300;
  const ng = c.createGain(); ng.gain.value = 0.4;
  n.connect(f); f.connect(ng); ng.connect(master); n.start();
  // Cricket oscillators
  const nodes = [n];
  for (let i = 0; i < 4; i++) {
    const osc = c.createOscillator(); osc.type = 'sine';
    osc.frequency.value = 4000 + Math.random() * 2000;
    const g = c.createGain(); g.gain.value = 0;
    osc.connect(g); g.connect(master); osc.start(); nodes.push(osc);
    const chirp = () => {
      const now = c.currentTime;
      g.gain.setValueAtTime(0, now);
      for (let j = 0; j < 3 + Math.floor(Math.random() * 4); j++) {
        g.gain.setValueAtTime(0.08, now + j * 0.06);
        g.gain.setValueAtTime(0, now + j * 0.06 + 0.03);
      }
      setTimeout(chirp, 500 + Math.random() * 2000);
    };
    setTimeout(chirp, Math.random() * 3000);
  }
  return { nodes, master };
}

// ===== Meditation Sounds =====

// Tibetan singing bowls — slow rising/falling harmonic tones
function startSingingBowls(volume) {
  const c = getCtx();
  const master = c.createGain();
  master.gain.value = volume * 0.35;
  master.connect(c.destination);
  const nodes = [];
  // Base frequencies (Hz) — warm pentatonic bowl notes
  const bowls = [174.6, 220.0, 261.6, 329.6, 392.0];
  bowls.forEach((freq, i) => {
    const osc = c.createOscillator();
    osc.type = 'sine';
    osc.frequency.value = freq;
    // Add slow vibrato for authentic bowl shimmer
    const lfo = c.createOscillator();
    lfo.frequency.value = 4 + Math.random() * 2;
    const lfoGain = c.createGain(); lfoGain.gain.value = 1.5;
    lfo.connect(lfoGain); lfoGain.connect(osc.frequency);
    const g = c.createGain(); g.gain.value = 0;
    osc.connect(g); g.connect(master);
    osc.start(); lfo.start();
    // Strike pattern — each bowl rings in turn
    const strike = () => {
      const now = c.currentTime;
      g.gain.cancelScheduledValues(now);
      g.gain.setValueAtTime(0, now);
      g.gain.linearRampToValueAtTime(0.15, now + 0.3);
      g.gain.exponentialRampToValueAtTime(0.001, now + 6);
      setTimeout(strike, 8000 + Math.random() * 8000);
    };
    setTimeout(strike, i * 2500);
    nodes.push(osc, lfo);
  });
  return { nodes, master };
}

// Binaural alpha beat (10 Hz) — L ear 220 Hz, R ear 230 Hz
function startBinauralAlpha(volume) {
  const c = getCtx();
  const master = c.createGain();
  master.gain.value = volume * 0.12;
  master.connect(c.destination);
  const merger = c.createChannelMerger(2);
  merger.connect(master);
  const leftOsc = c.createOscillator();
  leftOsc.type = 'sine';
  leftOsc.frequency.value = 220;
  const leftGain = c.createGain(); leftGain.gain.value = 0.8;
  leftOsc.connect(leftGain); leftGain.connect(merger, 0, 0);
  const rightOsc = c.createOscillator();
  rightOsc.type = 'sine';
  rightOsc.frequency.value = 230; // 10 Hz difference = alpha
  const rightGain = c.createGain(); rightGain.gain.value = 0.8;
  rightOsc.connect(rightGain); rightGain.connect(merger, 0, 1);
  leftOsc.start(); rightOsc.start();
  // Add subtle pink noise bed for comfort
  const n = createNoise('pink');
  const nFilter = c.createBiquadFilter(); nFilter.type = 'lowpass'; nFilter.frequency.value = 600;
  const nGain = c.createGain(); nGain.gain.value = 0.15;
  n.connect(nFilter); nFilter.connect(nGain); nGain.connect(master); n.start();
  return { nodes: [leftOsc, rightOsc, n], master };
}

// Om chant — sustained fundamental + overtones
function startOmChant(volume) {
  const c = getCtx();
  const master = c.createGain();
  master.gain.value = volume * 0.3;
  master.connect(c.destination);
  const nodes = [];
  // Fundamental A2 = 110 Hz with 5th + octave overtones
  [110, 165, 220, 275].forEach((freq, i) => {
    const osc = c.createOscillator();
    osc.type = i === 0 ? 'sawtooth' : 'sine';
    osc.frequency.value = freq;
    const g = c.createGain();
    g.gain.value = 0;
    const lpf = c.createBiquadFilter();
    lpf.type = 'lowpass';
    lpf.frequency.value = 400 + i * 200;
    osc.connect(lpf); lpf.connect(g); g.connect(master);
    osc.start();
    // Breathing envelope — 8s in, 2s hold, 6s out
    const cycle = () => {
      const now = c.currentTime;
      const target = 0.15 / (i + 1);
      g.gain.cancelScheduledValues(now);
      g.gain.setValueAtTime(g.gain.value, now);
      g.gain.linearRampToValueAtTime(target, now + 8);
      g.gain.linearRampToValueAtTime(target * 0.9, now + 10);
      g.gain.linearRampToValueAtTime(0, now + 16);
      setTimeout(cycle, 16000 + Math.random() * 2000);
    };
    setTimeout(cycle, i * 300);
    nodes.push(osc);
  });
  return { nodes, master };
}

// Gentle piano pad — soft chord swells
function startPianoPad(volume) {
  const c = getCtx();
  const master = c.createGain();
  master.gain.value = volume * 0.3;
  master.connect(c.destination);
  const nodes = [];
  // C major 9 chord: C E G B D
  const chord = [261.6, 329.6, 392.0, 493.9, 587.3];
  chord.forEach((freq, i) => {
    const osc = c.createOscillator();
    osc.type = 'triangle';
    osc.frequency.value = freq;
    const g = c.createGain(); g.gain.value = 0;
    const lpf = c.createBiquadFilter(); lpf.type = 'lowpass'; lpf.frequency.value = 2000;
    osc.connect(lpf); lpf.connect(g); g.connect(master);
    osc.start();
    // Slow swell — each voice pulses at slightly different rate
    const swell = () => {
      const now = c.currentTime;
      const peak = 0.09 / (i + 1);
      g.gain.cancelScheduledValues(now);
      g.gain.setValueAtTime(g.gain.value, now);
      g.gain.linearRampToValueAtTime(peak, now + 4 + Math.random() * 2);
      g.gain.linearRampToValueAtTime(0, now + 12 + Math.random() * 4);
      setTimeout(swell, 10000 + Math.random() * 6000);
    };
    setTimeout(swell, i * 700);
    nodes.push(osc);
  });
  return { nodes, master };
}

// Wind chimes — random pentatonic tinkles
function startChimes(volume) {
  const c = getCtx();
  const master = c.createGain();
  master.gain.value = volume * 0.3;
  master.connect(c.destination);
  // Pentatonic scale — never dissonant
  const notes = [523.3, 587.3, 659.3, 784.0, 880.0, 1046.5, 1174.7];
  const nodes = [];
  const playChime = () => {
    const freq = notes[Math.floor(Math.random() * notes.length)];
    const osc = c.createOscillator();
    osc.type = 'sine';
    osc.frequency.value = freq;
    const g = c.createGain(); g.gain.value = 0;
    osc.connect(g); g.connect(master);
    const now = c.currentTime;
    g.gain.setValueAtTime(0, now);
    g.gain.linearRampToValueAtTime(0.22, now + 0.02);
    g.gain.exponentialRampToValueAtTime(0.001, now + 3.5);
    osc.start(now);
    osc.stop(now + 4);
    setTimeout(playChime, 1500 + Math.random() * 3500);
  };
  setTimeout(playChime, 500);
  return { nodes, master };
}

// Deep drone — ultra-low sustained meditation drone
function startDeepDrone(volume) {
  const c = getCtx();
  const master = c.createGain();
  master.gain.value = volume * 0.4;
  master.connect(c.destination);
  const nodes = [];
  // Fundamental + perfect fifth, very low
  [55, 82.4].forEach((freq) => {
    const osc = c.createOscillator();
    osc.type = 'sine';
    osc.frequency.value = freq;
    const g = c.createGain(); g.gain.value = 0.4;
    // Slow volume breathing
    const lfo = c.createOscillator();
    lfo.frequency.value = 0.08;
    const lfoGain = c.createGain(); lfoGain.gain.value = 0.15;
    lfo.connect(lfoGain); lfoGain.connect(g.gain);
    osc.connect(g); g.connect(master);
    osc.start(); lfo.start();
    nodes.push(osc, lfo);
  });
  // Thin higher shimmer pad
  const shimmer = c.createOscillator();
  shimmer.type = 'sine';
  shimmer.frequency.value = 330;
  const shGain = c.createGain(); shGain.gain.value = 0.04;
  shimmer.connect(shGain); shGain.connect(master);
  shimmer.start();
  nodes.push(shimmer);
  return { nodes, master };
}

const SOUND_GENERATORS = {
  rain: startRain,
  cafe: startCafe,
  lofi: startLofi,
  fireplace: startFireplace,
  forest: startForest,
  ocean: startOcean,
  wind: startWind,
  birds: startBirds,
  holiday: startHoliday,
  spring: startSpring,
  summer: startSummer,
  autumn: startAutumn,
  night: startNight,
  singing_bowls: startSingingBowls,
  binaural_alpha: startBinauralAlpha,
  om_chant: startOmChant,
  piano_pad: startPianoPad,
  chimes: startChimes,
  deep_drone: startDeepDrone,
};

export function startAmbient(soundKey, volume = 0.5) {
  stopAmbient(soundKey);
  const generator = SOUND_GENERATORS[soundKey];
  if (!generator) return;
  try {
    activeNodes[soundKey] = generator(volume);
  } catch (e) {
    console.error('Failed to start ambient:', e);
  }
}

export function stopAmbient(soundKey) {
  const active = activeNodes[soundKey];
  if (!active) return;
  try {
    active.master.gain.linearRampToValueAtTime(0, getCtx().currentTime + 0.5);
    setTimeout(() => {
      active.nodes.forEach(n => { try { n.stop(); } catch { /* already stopped */ } });
      active.master.disconnect();
    }, 600);
  } catch { /* AudioContext teardown race */ }
  delete activeNodes[soundKey];
}

export function setAmbientVolume(soundKey, volume) {
  const active = activeNodes[soundKey];
  if (active) {
    active.master.gain.linearRampToValueAtTime(volume * 0.4, getCtx().currentTime + 0.2);
  }
}

export function stopAllAmbient() {
  Object.keys(activeNodes).forEach(stopAmbient);
}

export function isAmbientPlaying(soundKey) {
  return !!activeNodes[soundKey];
}

export const AMBIENT_SOUNDS = [
  { key: 'rain', label: 'Rain', emoji: '🌧️', color: '#A2D2FF', category: 'nature' },
  { key: 'cafe', label: 'Cafe', emoji: '☕', color: '#FDF0D5', category: 'nature' },
  { key: 'lofi', label: 'Lo-fi', emoji: '🎵', color: '#CDB4DB', category: 'nature' },
  { key: 'fireplace', label: 'Fire', emoji: '🔥', color: '#FFC8DD', category: 'nature' },
  { key: 'forest', label: 'Forest', emoji: '🌲', color: '#B9FBC0', category: 'nature' },
  { key: 'ocean', label: 'Ocean', emoji: '🌊', color: '#A2D2FF', category: 'nature' },
  { key: 'wind', label: 'Wind', emoji: '💨', color: '#E8E8E8', category: 'nature' },
  { key: 'birds', label: 'Birds', emoji: '🐦', color: '#FDF0D5', category: 'nature' },
  // Seasonal
  { key: 'holiday', label: 'Holiday', emoji: '🎄', color: '#FF6B6B', category: 'seasonal' },
  { key: 'spring', label: 'Spring', emoji: '🌸', color: '#FFC8DD', category: 'seasonal' },
  { key: 'summer', label: 'Beach', emoji: '🏖️', color: '#FDF0D5', category: 'seasonal' },
  { key: 'autumn', label: 'Autumn', emoji: '🍂', color: '#F0C040', category: 'seasonal' },
  { key: 'night', label: 'Night', emoji: '🌙', color: '#CDB4DB', category: 'seasonal' },

  { key: 'singing_bowls', label: 'Klangskål', emoji: '🪷', color: '#CDB4DB', category: 'meditation' },
  { key: 'om_chant', label: 'Om-mantra', emoji: '🕉️', color: '#FDF0D5', category: 'meditation' },
  { key: 'binaural_alpha', label: 'Alfa-vågor', emoji: '🌀', color: '#A2D2FF', category: 'meditation' },
  { key: 'piano_pad', label: 'Piano-dyna', emoji: '🎹', color: '#FFC8DD', category: 'meditation' },
  { key: 'chimes', label: 'Vindspel', emoji: '🎐', color: '#B9FBC0', category: 'meditation' },
  { key: 'deep_drone', label: 'Djup drone', emoji: '🧘', color: '#8B8FA8', category: 'meditation' },
];
